import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { IListItem } from 'types/Contract';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderChapterDataGql } from '../../bimWorkOrderQueries';
import { bimWorkOrderChapterLabel } from '../../types';

export const useBimWorkOrderPriceCounter = ({
    workOrderId,
    workOrderSectionOrChapter,
    constructionTypeId,
    workOrderRootObjectId,
}: {
    workOrderId: number | undefined;
    workOrderSectionOrChapter: IListItem<string> | undefined;
    workOrderRootObjectId: number | undefined;
    constructionTypeId: number | undefined;
}) => {
    const isValidToDisplay = workOrderId && constructionTypeId && workOrderRootObjectId;
    const dispatch = useDispatch();

    const [bimWorkOrderChapterData, , , bimWorkOrderChapterDataRequest] = useRequest({
        requestId: 'bimWorkOrderChapterData',
        query: bimWorkOrderChapterDataGql,
        withEffect: false,
    });

    const [resetBimPriceListCounterData] = useResetStoreData({
        requestId: 'bimWorkOrderChapterData',
    });

    const makeRequestPriceListData = useCallback(() => {
        dispatch(bimWorkOrderChapterDataRequest({ workOrderId, constructionTypeId, workOrderRootObjectId }));
    }, [bimWorkOrderChapterDataRequest, workOrderRootObjectId, constructionTypeId, dispatch, workOrderId]);

    useEffect(() => {
        if (isValidToDisplay) {
            makeRequestPriceListData();
        }
    }, [workOrderRootObjectId, constructionTypeId, isValidToDisplay, makeRequestPriceListData, workOrderId]);

    const priceListCounter = useMemo(
        () =>
            isValidToDisplay
                ? bimWorkOrderChapterData?.bimWorkOrderChapterData?.data?.find(
                      (el) => el?.chapter === workOrderSectionOrChapter?.id,
                  )?.pricesCount
                : undefined,
        [bimWorkOrderChapterData?.bimWorkOrderChapterData?.data, isValidToDisplay, workOrderSectionOrChapter?.id],
    );

    const chaptersTooltip = useMemo(() => {
        if (!bimWorkOrderChapterData?.bimWorkOrderChapterData?.data || !isValidToDisplay) {
            return undefined;
        }

        return bimWorkOrderChapterData?.bimWorkOrderChapterData?.data.map((el) => ({
            chapter: el?.chapter ? bimWorkOrderChapterLabel[el.chapter] : null,
            priceCount: el?.pricesCount,
        }));
    }, [bimWorkOrderChapterData?.bimWorkOrderChapterData?.data, isValidToDisplay]);

    const workOrderHasNoPrices = useMemo(
        () => bimWorkOrderChapterData?.bimWorkOrderChapterData?.data?.every((chapter) => chapter?.pricesCount === 0),
        [bimWorkOrderChapterData?.bimWorkOrderChapterData?.data],
    );

    return {
        priceListCounter,
        chaptersTooltip,
        resetBimPriceListCounterData,
        makeRequestPriceListData,
        workOrderHasNoPrices,
    };
};
